/**
 * De gegevens die op een factuur moeten staan.
 *
 * Quality Cashless is de verkoper en maakt de inkoopfactuur van de kraam zelf
 * op (regel 12, selfbilling). Zo'n factuur is zonder het BTW-nummer van de
 * leverancier niet geldig: de Belastingdienst accepteert hem niet en de BTW is
 * niet terug te vragen. Dat merk je niet op de dag zelf maar bij de aangifte,
 * als de facturen al verstuurd zijn.
 *
 * Daarom controleren we het hier, vóór een factuur wordt gemaakt, en weigeren
 * we liever dan een ongeldig papier te produceren.
 */

// Alleen het type, zodat er geen kringetje ontstaat: factuur.ts gebruikt dit
// bestand voor de controle, dit bestand gebruikt daar alleen de vorm van.
import type { Partij } from './factuur.js';

/**
 * Een Nederlands BTW-identificatienummer.
 *
 * De vorm is NL, negen tekens, een B en twee cijfers.
 *
 * Tot 2020 waren die negen tekens altijd cijfers, met een elfproef erop. Sinds
 * 2020 krijgt een eenmanszaak een willekeurig nummer waar geen elfproef op
 * zit. Bestaat dat nummer toevallig uit negen cijfers, dan is het niet te
 * onderscheiden van een oud nummer.
 *
 * Daarom blokkeert de elfproef niets. Een kraamhouder die zijn geld niet
 * krijgt omdat wij een controle uitvoeren die op zijn nummer niet van
 * toepassing is, is een ergere fout dan een typefout die bij de aangifte aan
 * het licht komt. De elfproef geeft alleen een waarschuwing.
 */
const BTW_VORM = /^NL[0-9A-Z+*]{9}B[0-9]{2}$/;

export function normaliseerBtwNummer(invoer: string): string {
  return invoer.replace(/[\s.-]/g, '').toUpperCase();
}

export function isGeldigBtwNummer(invoer: string | null | undefined): boolean {
  if (invoer == null) return false;
  return BTW_VORM.test(normaliseerBtwNummer(invoer));
}

function doorstaatElfproef(negenCijfers: string): boolean {
  let som = 0;
  for (let i = 0; i < 8; i += 1) {
    som += Number(negenCijfers[i]) * (9 - i);
  }
  return som % 11 === Number(negenCijfers[8]);
}

/**
 * Ziet er dit nummer uit als een typefout?
 *
 * Alleen waar als het uit negen cijfers bestaat en de elfproef niet doorstaat.
 * Gebruik dit om te waarschuwen, niet om te blokkeren.
 */
export function lijktOpTypfoutBtwNummer(invoer: string | null | undefined): boolean {
  if (!isGeldigBtwNummer(invoer)) return false;
  const kern = normaliseerBtwNummer(invoer as string).slice(2, 11);
  if (!/^[0-9]{9}$/.test(kern)) return false;
  return !doorstaatElfproef(kern);
}

/** Een KvK-nummer is acht cijfers. */
export function isGeldigKvkNummer(invoer: string | null | undefined): boolean {
  if (invoer == null) return false;
  return /^[0-9]{8}$/.test(invoer.replace(/[\s.-]/g, ''));
}

/**
 * Wat er aan een partij ontbreekt voordat er een factuur op mag.
 *
 * Geeft zinnen terug die je zo aan een gebruiker kunt laten zien, niet
 * veldnamen. Leeg betekent: deze partij is compleet.
 */
export function watOntbreektVoorFactuur(partij: Partij): string[] {
  const ontbreekt: string[] = [];

  if (partij.naam.trim() === '') {
    ontbreekt.push('de bedrijfsnaam');
  }
  if (!isGeldigBtwNummer(partij.btwNummer)) {
    ontbreekt.push(
      partij.btwNummer == null || partij.btwNummer.trim() === ''
        ? 'het BTW-nummer'
        : 'een geldig BTW-nummer',
    );
  }
  if (!isGeldigKvkNummer(partij.kvkNummer)) {
    ontbreekt.push(
      partij.kvkNummer == null || partij.kvkNummer.trim() === ''
        ? 'het KvK-nummer'
        : 'een geldig KvK-nummer',
    );
  }

  return ontbreekt;
}

export function isCompleetVoorFactuur(partij: Partij): boolean {
  return watOntbreektVoorFactuur(partij).length === 0;
}

/** Eén zin die je aan een organisator of kraam kunt laten zien. */
export function beschrijfOntbrekend(wieOfWat: string, ontbreekt: readonly string[]): string {
  if (ontbreekt.length === 0) return '';
  if (ontbreekt.length === 1) return `Bij ${wieOfWat} ontbreekt ${ontbreekt[0]}.`;
  const laatste = ontbreekt[ontbreekt.length - 1];
  const eerdere = ontbreekt.slice(0, -1).join(', ');
  return `Bij ${wieOfWat} ontbreken ${eerdere} en ${laatste}.`;
}
